import { parseApiEnvelope } from "./liquidacion-reportes.contract";

function isPlainObject(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function normalizeList(response) {
  return parseApiEnvelope(response);
}

function normalizeNumber(value) {
  if (value === null || value === undefined || value === "") return 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function normalizeNullableNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeText(value) {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

function formatApiDate(value) {
  const text = normalizeText(value);

  if (!text || text.startsWith("0001-01-01")) {
    return "";
  }

  return text.length >= 10 ? text.slice(0, 10) : text;
}

function pick(row, ...keys) {
  for (const key of keys) {
    if (row[key] !== undefined && row[key] !== null) {
      return row[key];
    }
  }

  return undefined;
}

function resolveUrlPdf(row) {
  const rawUrl = normalizeText(
    pick(
      row,
      "urlPdf",
      "url_pdf",
      "UrlPdf",
      "linkPdf",
      "link_pdf",
      "pdf",
      "pdfUrl",
      "archivoPdf",
      "archivo_pdf",
    ),
  );
  if (
    !rawUrl ||
    rawUrl === "-" ||
    rawUrl.toLowerCase() === "null" ||
    rawUrl.toLowerCase() === "undefined"
  ) {
    return "";
  }
  return rawUrl;
}

/** Mapeo desde contrato API resumen (data: T[]). */
export function mapApiResumenRow(row) {
  if (!isPlainObject(row)) return row;

  const costoSinIgv = pick(
    row,
    "costoTotalSinIgv",
    "costo_total_sin_igv",
    "costoSinIgv",
  );
  const costoConIgv = pick(
    row,
    "costoTotalConIgv",
    "costo_total_con_igv",
    "costoConIgv",
  );
  const numeroOc = normalizeText(pick(row, "numeroOc", "numero_oc", "oc"));
  const urlPdf = resolveUrlPdf(row);

  return {
    codigoTienda: normalizeNumber(pick(row, "codigoTienda", "codigo_tienda", "codTienda")),
    tienda: normalizeText(row.tienda),
    usuario: normalizeText(row.usuario),
    proveedor: normalizeText(row.proveedor),
    corteLiquidacion: normalizeText(
      pick(row, "corteDeLiquidacion", "corte_de_liquidacion", "corteLiquidacion"),
    ),
    numeroOc,
    migo: normalizeText(pick(row, "migo", "migoNumero", "nroMigo", "nro_migo")),
    urlPdf,
    archivoPdf: urlPdf,
    costoSinIgv: normalizeNumber(costoSinIgv),
    costoConIgv: normalizeNumber(costoConIgv),
    penalidad: normalizeNullableNumber(row.penalidad),
    motivoPenalidad: normalizeText(
      pick(
        row,
        "penalidadMotivo",
        "motivoPenalidad",
        "motivo_penalidad",
        "motivoPenalidades",
      ),
    ),
    penalidades: Array.isArray(row.penalidades)
      ? row.penalidades
      : Array.isArray(row.motivosPenalidad)
        ? row.motivosPenalidad
        : [],
    totalPorFactSinIgv: normalizeNumber(
      pick(
        row,
        "totalPorFactSinIgv",
        "total_por_fact_sin_igv",
        "totalFacturarSinIgv",
        "totalPorFacturarSinIgv",
      ),
    ),
    igv: normalizeNullableNumber(row.igv),
    totalPorFactConIgv: normalizeNumber(
      pick(
        row,
        "totalPorFactConIgv",
        "total_por_fact_con_igv",
        "totalFacturarConIgv",
        "totalPorFacturarConIgv",
      ),
    ),
  };
}

/** Mapeo desde contrato API detallado (data.datos). */
export function mapApiDetalladoRow(row) {
  if (!isPlainObject(row)) return row;

  return {
    id: normalizeNumber(row.id),
    codigoTienda: normalizeNumber(pick(row, "codigoTienda", "codigo_tienda")),
    tienda: normalizeText(row.tienda),
    usuario: normalizeText(row.usuario),
    proveedor: normalizeText(row.proveedor),
    corteLiquidacion: normalizeText(
      pick(row, "corteDeLiquidacion", "corte_de_liquidacion", "corteLiquidacion"),
    ),
    nroOs: normalizeText(pick(row, "numeroOs", "nroOs", "nro_obs", "nroObs")),
    esAdicional: Boolean(row.esAdicional),
    orden: normalizeText(row.orden),
    estado: normalizeText(row.estado),
    cliente: normalizeText(row.cliente),
    skuServ: normalizeText(pick(row, "skuServicio", "skuServ", "sku_serv")),
    servicio: normalizeText(pick(row, "servicioDescripcion", "servicio")),
    skuProd: normalizeText(pick(row, "skuProducto", "skuProd", "sku_prod")),
    producto: normalizeText(pick(row, "productoDescripcion", "producto")),
    cantidad: normalizeNumber(row.cantidad),
    fechaServicio: formatApiDate(
      pick(row, "fecServicio", "fechaServicio", "fec_servicio"),
    ),
    costoUnitarioSinIgv: normalizeNumber(
      pick(row, "costoUniSinIgv", "costoUnitarioSinIgv", "costo_uni_sin_igv"),
    ),
    costoUnitarioConIgv: normalizeNumber(
      pick(row, "costoUniConIgv", "costoUnitarioConIgv", "costo_uni_con_igv"),
    ),
    costoTotalSinIgv: normalizeNumber(
      pick(row, "costoTotalSinIgv", "costo_total_sin_igv"),
    ),
    costoTotalConIgv: normalizeNumber(
      pick(row, "costoTotalConIgv", "costo_total_con_igv"),
    ),
    penalidad: normalizeNullableNumber(row.penalidad),
    motivoPenalidad: normalizeText(
      pick(
        row,
        "penalidadMotivo",
        "motivoPenalidad",
        "motivo_penalidad",
        "motivoPenalidades",
      ),
    ),
    penalidades: Array.isArray(row.penalidades)
      ? row.penalidades
      : Array.isArray(row.motivosPenalidad)
        ? row.motivosPenalidad
        : [],
    costoFinalSinIgv: normalizeNumber(
      pick(row, "costoFinalSinIgv", "costo_final_sin_igv"),
    ),
    costoFinalConIgv: normalizeNumber(
      pick(row, "costoFinalConIgv", "costo_final_con_igv"),
    ),
  };
}

function isApiResumenRow(row) {
  return (
    row.costoTotalSinIgv !== undefined ||
    row.costo_total_sin_igv !== undefined ||
    row.corteDeLiquidacion !== undefined ||
    row.totalPorFactSinIgv !== undefined ||
    row.totalPorFactConIgv !== undefined ||
    row.codigoTienda !== undefined
  );
}

function isApiDetalladoRow(row) {
  return (
    row.numeroOs !== undefined ||
    row.servicioDescripcion !== undefined ||
    row.productoDescripcion !== undefined ||
    row.costoUniSinIgv !== undefined ||
    row.costo_uni_sin_igv !== undefined ||
    row.costoUnitarioSinIgv !== undefined ||
    row.nro_obs !== undefined
  );
}

/** Mapeo mock / vista (camelCase). */
export function mapResumenRow(row) {
  if (!isPlainObject(row)) return row;

  if (isApiResumenRow(row)) {
    return mapApiResumenRow(row);
  }

  const numeroOc = normalizeText(pick(row, "numeroOc", "numero_oc", "oc"));
  const urlPdf = resolveUrlPdf(row);

  return {
    id: normalizeNumber(row.id),
    idTienda: normalizeNumber(row.idTienda ?? row.codTienda),
    idProv: normalizeNumber(row.idProv),
    estado: normalizeText(row.estado),
    usuario: normalizeText(row.usuario),
    proveedor: normalizeText(row.proveedor),
    corteLiquidacion: normalizeText(row.corteLiquidacion),
    numeroOc,
    migo: normalizeText(pick(row, "migo", "migoNumero", "nroMigo", "nro_migo")),
    urlPdf,
    archivoPdf: urlPdf,
    costoSinIgv: normalizeNumber(row.costoSinIgv ?? row.costoTotalSinIgv),
    costoConIgv: normalizeNumber(row.costoConIgv ?? row.costoTotalConIgv),
    penalidad: normalizeNullableNumber(row.penalidad),
    totalPorFactSinIgv: normalizeNumber(
      row.totalPorFactSinIgv ?? row.totalFacturarSinIgv,
    ),
    igv: normalizeNullableNumber(row.igv),
    totalPorFactConIgv: normalizeNumber(
      row.totalPorFactConIgv ?? row.totalFacturarConIgv,
    ),
  };
}

/** Mapeo mock / vista (camelCase). */
export function mapDetalladoRow(row) {
  if (!isPlainObject(row)) return row;

  if (isApiDetalladoRow(row)) {
    return mapApiDetalladoRow(row);
  }

  return {
    id: normalizeNumber(row.id),
    idTienda: normalizeNumber(row.idTienda ?? row.codTienda),
    idProv: normalizeNumber(row.idProv),
    tienda: normalizeText(row.tienda),
    usuario: normalizeText(row.usuario),
    proveedor: normalizeText(row.proveedor),
    corteLiquidacion: normalizeText(row.corteLiquidacion),
    nroOs: normalizeText(row.nroOs),
    estado: normalizeText(row.estado),
    cliente: normalizeText(row.cliente),
    skuServ: normalizeText(row.skuServ),
    servicio: normalizeText(row.servicio),
    skuProd: normalizeText(row.skuProd),
    producto: normalizeText(row.producto),
    cantidad: normalizeNumber(row.cantidad),
    fechaServicio: normalizeText(row.fechaServicio),
    costoUnitarioSinIgv: normalizeNumber(row.costoUnitarioSinIgv),
    costoUnitarioConIgv: normalizeNumber(row.costoUnitarioConIgv),
    costoTotalSinIgv: normalizeNumber(row.costoTotalSinIgv),
    costoTotalConIgv: normalizeNumber(row.costoTotalConIgv),
    penalidad: normalizeNullableNumber(row.penalidad),
    motivoPenalidad: normalizeText(
      pick(row, "penalidadMotivo", "motivoPenalidad", "motivo_penalidad"),
    ),
    costoFinalSinIgv: normalizeNumber(row.costoFinalSinIgv),
    costoFinalConIgv: normalizeNumber(row.costoFinalConIgv),
  };
}

export function mapResumenResponse(response) {
  return normalizeList(response).map(mapResumenRow);
}

export function mapDetalladoResponse(response) {
  return normalizeList(response).map(mapDetalladoRow);
}

export function mapApiResumenResponse(response) {
  return normalizeList(response).map(mapApiResumenRow);
}

export function mapApiDetalladoResponse(response) {
  return normalizeList(response).map(mapApiDetalladoRow);
}
